import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import api from '../lib/api';

const WarehouseTransfer = ({ user }) => {
  const [transfers, setTransfers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [viewTransfer, setViewTransfer] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Form state
  const [fromWarehouse, setFromWarehouse] = useState('');
  const [toWarehouse, setToWarehouse] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Add item state
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [itemQty, setItemQty] = useState('');

  const canCreate = ['admin', 'manager', 'inventory_staff'].includes(user?.role);
  const canReceive = ['admin', 'manager', 'inventory_staff'].includes(user?.role);
  const canCancel = ['admin', 'manager'].includes(user?.role);

  useKeyboardShortcuts({
    onNew: () => { if (canCreate && !isCreateOpen) setIsCreateOpen(true); },
    onEscape: () => { if (viewTransfer) setViewTransfer(null); if (isCreateOpen) resetForm(); },
  });

  const loadData = async () => {
    try {
      const [tRes, wRes, pRes] = await Promise.all([
        api.get('/warehouse-transfers'),
        api.get('/warehouses'),
        api.get('/products'),
      ]);
      setTransfers(tRes.data);
      setWarehouses(wRes.data);
      setProducts(pRes.data);
    } catch {
      toast.error('Failed to load data');
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  // Get available sizes/colors for selected product
  const selectedProductData = useMemo(() => products.find(p => p.id === selectedProduct), [products, selectedProduct]);
  const availableSizes = useMemo(() => {
    if (!selectedProductData) return [];
    return [...new Set(selectedProductData.skus.map(s => s.size))];
  }, [selectedProductData]);
  const availableColors = useMemo(() => {
    if (!selectedProductData || !selectedSize) return [];
    return [...new Set(selectedProductData.skus.filter(s => s.size === selectedSize).map(s => s.color))];
  }, [selectedProductData, selectedSize]);

  const currentSku = useMemo(() => {
    if (!selectedProductData || !selectedSize || !selectedColor) return null;
    return selectedProductData.skus.find(s => s.size === selectedSize && s.color === selectedColor);
  }, [selectedProductData, selectedSize, selectedColor]);

  const resetForm = () => {
    setIsCreateOpen(false);
    setFromWarehouse('');
    setToWarehouse('');
    setReason('');
    setNotes('');
    setItems([]);
    setSelectedProduct('');
    setSelectedSize('');
    setSelectedColor('');
    setItemQty('');
  };

  const addItem = () => {
    if (!selectedProduct || !selectedSize || !selectedColor || !itemQty || parseInt(itemQty) <= 0) {
      toast.error('Fill all item fields with a valid quantity');
      return;
    }
    const product = products.find(p => p.id === selectedProduct);
    const exists = items.find(i => i.product_id === selectedProduct && i.size === selectedSize && i.color === selectedColor);
    if (exists) {
      toast.error('This SKU is already added. Remove it first.');
      return;
    }
    setItems(prev => [...prev, {
      product_id: selectedProduct,
      design_code: product.design_code,
      size: selectedSize,
      color: selectedColor,
      qty: parseInt(itemQty),
      sku_code: currentSku?.sku_code || '',
      current_stock: currentSku?.current_stock || 0,
    }]);
    setSelectedProduct('');
    setSelectedSize('');
    setSelectedColor('');
    setItemQty('');
  };

  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const submitTransfer = async () => {
    if (!fromWarehouse || !toWarehouse) { toast.error('Select both warehouses'); return; }
    if (fromWarehouse === toWarehouse) { toast.error('Source and destination must differ'); return; }
    if (items.length === 0) { toast.error('Add at least one item'); return; }
    setSubmitting(true);
    try {
      await api.post('/warehouse-transfers', {
        from_warehouse_id: fromWarehouse,
        to_warehouse_id: toWarehouse,
        reason,
        notes,
        items: items.map(({ design_code, product_id, size, color, qty }) => ({
          design_code, product_id, size, color, qty,
        })),
      });
      toast.success('Transfer created successfully');
      resetForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create transfer');
    }
    setSubmitting(false);
  };

  const handleReceive = async (id) => {
    try {
      await api.put(`/warehouse-transfers/${id}/receive`);
      toast.success('Transfer marked as received');
      loadData();
      if (viewTransfer?.id === id) {
        const res = await api.get(`/warehouse-transfers/${id}`);
        setViewTransfer(res.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to receive transfer');
    }
  };

  const handleCancel = async (id) => {
    try {
      await api.put(`/warehouse-transfers/${id}/cancel`);
      toast.success('Transfer cancelled');
      loadData();
      if (viewTransfer?.id === id) {
        const res = await api.get(`/warehouse-transfers/${id}`);
        setViewTransfer(res.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel transfer');
    }
  };

  const statusColor = (s) => {
    switch (s) {
      case 'pending': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'received': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filtered = useMemo(() => {
    let list = transfers;
    if (filterStatus !== 'all') list = list.filter(t => t.status === filterStatus);
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(t =>
        t.transfer_number?.toLowerCase().includes(q) ||
        t.from_warehouse_name?.toLowerCase().includes(q) ||
        t.to_warehouse_name?.toLowerCase().includes(q) ||
        t.items?.some(i => i.design_code?.toLowerCase().includes(q))
      );
    }
    return list;
  }, [transfers, filterStatus, searchTerm]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="warehouse-transfer-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="warehouse-transfer-title">Warehouse Transfers</h1>
          <p className="text-sm text-gray-500 mt-1">Manage stock movement between warehouses</p>
        </div>
        {canCreate && (
          <Button onClick={() => setIsCreateOpen(true)} data-testid="create-transfer-btn">
            + New Transfer
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total', value: transfers.length, cls: 'text-blue-700 bg-blue-50 border-blue-100' },
          { label: 'Pending', value: transfers.filter(t => t.status === 'pending').length, cls: 'text-amber-700 bg-amber-50 border-amber-100' },
          { label: 'Received', value: transfers.filter(t => t.status === 'received').length, cls: 'text-emerald-700 bg-emerald-50 border-emerald-100' },
          { label: 'Cancelled', value: transfers.filter(t => t.status === 'cancelled').length, cls: 'text-red-700 bg-red-50 border-red-100' },
        ].map(s => (
          <Card key={s.label} className={`border ${s.cls}`}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{s.value}</p>
              <p className="text-xs font-medium mt-1">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Input
          placeholder="Search by transfer #, warehouse, design code..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="sm:max-w-xs"
          data-testid="transfer-search-input"
        />
        <div className="flex gap-2 flex-wrap">
          {['all', 'pending', 'received', 'cancelled'].map(s => (
            <Button
              key={s}
              variant={filterStatus === s ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterStatus(s)}
              data-testid={`filter-${s}-btn`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {/* Transfers Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Transfer #</TableHead>
                <TableHead>From</TableHead>
                <TableHead>To</TableHead>
                <TableHead className="text-center">Items</TableHead>
                <TableHead className="text-center">Total Qty</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12 text-gray-400">
                    {transfers.length === 0 ? 'No transfers yet. Create one to get started.' : 'No transfers match your filters.'}
                  </TableCell>
                </TableRow>
              ) : filtered.map(t => (
                <TableRow key={t.id} className="cursor-pointer hover:bg-gray-50" onClick={() => setViewTransfer(t)} data-testid={`transfer-row-${t.id}`}>
                  <TableCell className="font-mono font-semibold text-blue-700">{t.transfer_number}</TableCell>
                  <TableCell className="text-sm">{t.from_warehouse_name}</TableCell>
                  <TableCell className="text-sm">{t.to_warehouse_name}</TableCell>
                  <TableCell className="text-center">{t.items?.length || 0}</TableCell>
                  <TableCell className="text-center font-semibold">{t.total_qty}</TableCell>
                  <TableCell>
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusColor(t.status)}`}>
                      {t.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-gray-500">
                    {(() => { const d = new Date(t.created_at); return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getFullYear()).slice(-2)}`; })()}
                  </TableCell>
                  <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                    <div className="flex gap-1 justify-end">
                      {t.status === 'pending' && canReceive && (
                        <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={() => handleReceive(t.id)} data-testid={`receive-btn-${t.id}`}>
                          Receive
                        </Button>
                      )}
                      {t.status === 'pending' && canCancel && (
                        <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleCancel(t.id)} data-testid={`cancel-btn-${t.id}`}>
                          Cancel
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* View Transfer Dialog */}
      <Dialog open={!!viewTransfer} onOpenChange={() => setViewTransfer(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {viewTransfer?.transfer_number}
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${statusColor(viewTransfer?.status)}`}>
                {viewTransfer?.status}
              </span>
            </DialogTitle>
          </DialogHeader>
          {viewTransfer && (
            <div className="space-y-4" data-testid="transfer-detail-view">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500">From:</span> <span className="font-medium">{viewTransfer.from_warehouse_name}</span></div>
                <div><span className="text-gray-500">To:</span> <span className="font-medium">{viewTransfer.to_warehouse_name}</span></div>
                <div><span className="text-gray-500">Created by:</span> <span className="font-medium">{viewTransfer.created_by_name}</span></div>
                <div><span className="text-gray-500">Date:</span> <span className="font-medium">{new Date(viewTransfer.created_at).toLocaleDateString('en-IN')}</span></div>
                {viewTransfer.reason && <div className="col-span-2"><span className="text-gray-500">Reason:</span> <span className="font-medium">{viewTransfer.reason}</span></div>}
                {viewTransfer.notes && <div className="col-span-2"><span className="text-gray-500">Notes:</span> <span className="font-medium">{viewTransfer.notes}</span></div>}
                {viewTransfer.received_by_name && (
                  <div className="col-span-2"><span className="text-gray-500">Received by:</span> <span className="font-medium">{viewTransfer.received_by_name}</span> on {new Date(viewTransfer.received_at).toLocaleDateString('en-IN')}</div>
                )}
              </div>
              <div>
                <h4 className="text-sm font-semibold mb-2">Items ({viewTransfer.items?.length})</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Design Code</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Color</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {viewTransfer.items?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-mono font-semibold">{item.design_code}</TableCell>
                        <TableCell>{item.size}</TableCell>
                        <TableCell>{item.color}</TableCell>
                        <TableCell className="text-right font-semibold">{item.qty}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-gray-50">
                      <TableCell colSpan={3} className="text-right font-semibold">Total</TableCell>
                      <TableCell className="text-right font-bold">{viewTransfer.total_qty}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
              {viewTransfer.status === 'pending' && (
                <div className="flex gap-2 pt-2">
                  {canReceive && (
                    <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => handleReceive(viewTransfer.id)} data-testid="detail-receive-btn">
                      Mark as Received
                    </Button>
                  )}
                  {canCancel && (
                    <Button variant="outline" className="flex-1 text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleCancel(viewTransfer.id)} data-testid="detail-cancel-btn">
                      Cancel Transfer
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Transfer Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={(open) => { if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Warehouse Transfer</DialogTitle>
          </DialogHeader>
          <div className="space-y-5" data-testid="create-transfer-form">
            {/* Warehouse Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>From Warehouse *</Label>
                <select
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={fromWarehouse}
                  onChange={e => setFromWarehouse(e.target.value)}
                  data-testid="from-warehouse-select"
                >
                  <option value="">Select source...</option>
                  {warehouses.map(w => (
                    <option key={w.id} value={w.id} disabled={w.id === toWarehouse}>{w.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>To Warehouse *</Label>
                <select
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={toWarehouse}
                  onChange={e => setToWarehouse(e.target.value)}
                  data-testid="to-warehouse-select"
                >
                  <option value="">Select destination...</option>
                  {warehouses.map(w => (
                    <option key={w.id} value={w.id} disabled={w.id === fromWarehouse}>{w.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Reason & Notes */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Reason</Label>
                <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Stock rebalancing" className="mt-1" data-testid="transfer-reason-input" />
              </div>
              <div>
                <Label>Notes</Label>
                <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes..." className="mt-1" data-testid="transfer-notes-input" />
              </div>
            </div>

            {/* Add Item Section */}
            <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
              <h4 className="font-semibold text-sm text-gray-700">Add Item</h4>
              <div className="grid grid-cols-5 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs">Product</Label>
                  <select
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={selectedProduct}
                    onChange={e => { setSelectedProduct(e.target.value); setSelectedSize(''); setSelectedColor(''); }}
                    data-testid="item-product-select"
                  >
                    <option value="">Select product...</option>
                    {products.filter(p => p.status === 'active').map(p => (
                      <option key={p.id} value={p.id}>{p.design_code} - {p.category}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Size</Label>
                  <select
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={selectedSize}
                    onChange={e => { setSelectedSize(e.target.value); setSelectedColor(''); }}
                    disabled={!selectedProduct}
                    data-testid="item-size-select"
                  >
                    <option value="">Size...</option>
                    {availableSizes.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Color</Label>
                  <select
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={selectedColor}
                    onChange={e => setSelectedColor(e.target.value)}
                    disabled={!selectedSize}
                    data-testid="item-color-select"
                  >
                    <option value="">Color...</option>
                    {availableColors.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Qty {currentSku ? `(Stock: ${currentSku.current_stock})` : ''}</Label>
                  <Input
                    type="number"
                    min="1"
                    value={itemQty}
                    onChange={e => setItemQty(e.target.value)}
                    placeholder="Qty"
                    className="mt-1"
                    disabled={!selectedColor}
                    data-testid="item-qty-input"
                  />
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={addItem} disabled={!selectedProduct || !selectedSize || !selectedColor || !itemQty} data-testid="add-item-btn">
                + Add to Transfer
              </Button>
            </div>

            {/* Items List */}
            {items.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-2">Transfer Items ({items.length})</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Design Code</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Color</TableHead>
                      <TableHead>Stock</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-mono font-semibold">{item.design_code}</TableCell>
                        <TableCell>{item.size}</TableCell>
                        <TableCell>{item.color}</TableCell>
                        <TableCell className="text-gray-500">{item.current_stock}</TableCell>
                        <TableCell className="text-right font-semibold">{item.qty}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-7 w-7 p-0" onClick={() => removeItem(idx)} data-testid={`remove-item-${idx}`}>
                            X
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-gray-50">
                      <TableCell colSpan={4} className="text-right font-semibold">Total Qty</TableCell>
                      <TableCell className="text-right font-bold">{items.reduce((a, i) => a + i.qty, 0)}</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Submit */}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={resetForm} data-testid="cancel-create-btn">Cancel</Button>
              <Button onClick={submitTransfer} disabled={submitting || items.length === 0 || !fromWarehouse || !toWarehouse} data-testid="submit-transfer-btn">
                {submitting ? 'Creating...' : 'Create Transfer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WarehouseTransfer;
